import type { CalculatorConfig } from './types';

const cdn: CalculatorConfig = {
  type: 'cdn',
  title: 'CDN Cost Calculator',
  description: 'Estimate monthly CDN costs based on your bandwidth usage.',
  inputs: [
    { id: 'bandwidthGB', label: 'Bandwidth', unit: 'GB/mo', min: 0, max: 100000, step: 100, defaultValue: 1000 },
    { id: 'requestsM', label: 'HTTP Requests', unit: 'million/mo', min: 0, max: 1000, step: 1, defaultValue: 10 },
  ],
  providers: [
    {
      name: 'Cloudflare',
      color: '#f48120',
      calculate: () => 0,
      notes: () => 'Free plan includes unlimited bandwidth and requests. Pro ($20/mo) and Business ($200/mo) add features like image optimization and advanced WAF.',
    },
    {
      name: 'AWS CloudFront',
      color: '#ff9900',
      calculate: ({ bandwidthGB, requestsM }) => {
        // Tiered: first 10TB $0.085/GB, next 40TB $0.080, next 100TB $0.060, next 350TB $0.040
        let cost = 0;
        let remaining = bandwidthGB;
        const tiers = [
          { limit: 10240, rate: 0.085 },
          { limit: 40960, rate: 0.080 },
          { limit: 102400, rate: 0.060 },
          { limit: Infinity, rate: 0.040 },
        ];
        for (const tier of tiers) {
          const used = Math.min(remaining, tier.limit);
          cost += used * tier.rate;
          remaining -= used;
          if (remaining <= 0) break;
        }
        // HTTPS requests: $0.0100 per 10,000
        cost += requestsM * 1000000 * 0.0000010;
        return cost;
      },
      notes: () => 'Free data transfer from AWS origins (S3, ALB, EC2).',
    },
    {
      name: 'Azure CDN',
      color: '#0078d4',
      calculate: ({ bandwidthGB }) => {
        // Front Door Standard: first 10TB $0.081/GB, next 40TB $0.075
        let cost = 0;
        let remaining = bandwidthGB;
        const tiers = [
          { limit: 10240, rate: 0.081 },
          { limit: 40960, rate: 0.075 },
          { limit: Infinity, rate: 0.065 },
        ];
        for (const tier of tiers) {
          const used = Math.min(remaining, tier.limit);
          cost += used * tier.rate;
          remaining -= used;
          if (remaining <= 0) break;
        }
        // Base fee for Front Door Standard
        cost += 35;
        return cost;
      },
      notes: () => 'Azure Front Door Standard includes base fee of ~$35/mo.',
    },
    {
      name: 'Google Cloud CDN',
      color: '#4285f4',
      calculate: ({ bandwidthGB }) => {
        // $0.08/GB for first 10TB, $0.06 next
        let cost = 0;
        let remaining = bandwidthGB;
        const tiers = [
          { limit: 10240, rate: 0.08 },
          { limit: Infinity, rate: 0.06 },
        ];
        for (const tier of tiers) {
          const used = Math.min(remaining, tier.limit);
          cost += used * tier.rate;
          remaining -= used;
          if (remaining <= 0) break;
        }
        return cost;
      },
    },
  ],
};

// Per-tier pricing: [Standard, Infrequent Access, Cold, Archive]
// storage = $/GB/mo, retrieval = $/GB retrieved, putPer1K = $/1K PUT ops, getPer1K = $/1K GET ops
const r2Tiers = [
  { storage: 0.015, retrieval: 0, putPer1K: 0.0045, getPer1K: 0.00036 },     // Standard
  { storage: 0.01, retrieval: 0.01, putPer1K: 0.0045, getPer1K: 0.00036 },    // Infrequent Access
];
const s3Tiers = [
  { storage: 0.023, retrieval: 0, putPer1K: 0.005, getPer1K: 0.0004 },        // Standard
  { storage: 0.0125, retrieval: 0.01, putPer1K: 0.01, getPer1K: 0.001 },      // Standard-IA
  { storage: 0.004, retrieval: 0.03, putPer1K: 0.01, getPer1K: 0.01 },        // Glacier Instant Retrieval
  { storage: 0.00099, retrieval: 0.02, putPer1K: 0.05, getPer1K: 0.01 },      // Glacier Deep Archive
];
const azureTiers = [
  { storage: 0.018, retrieval: 0, putPer1K: 0.0055, getPer1K: 0.00044 },      // Hot
  { storage: 0.01, retrieval: 0.01, putPer1K: 0.011, getPer1K: 0.0011 },      // Cool
  { storage: 0.0045, retrieval: 0.03, putPer1K: 0.022, getPer1K: 0.0055 },    // Cold
  { storage: 0.00099, retrieval: 0.022, putPer1K: 0.011, getPer1K: 0.55 },    // Archive
];
const gcsTiers = [
  { storage: 0.020, retrieval: 0, putPer1K: 0.005, getPer1K: 0.0004 },        // Standard
  { storage: 0.010, retrieval: 0.01, putPer1K: 0.01, getPer1K: 0.001 },       // Nearline
  { storage: 0.004, retrieval: 0.02, putPer1K: 0.01, getPer1K: 0.005 },       // Coldline
  { storage: 0.0012, retrieval: 0.05, putPer1K: 0.05, getPer1K: 0.05 },       // Archive
];

function s3EgressCost(egressGB: number): number {
  let cost = 0;
  let remaining = Math.max(0, egressGB - 100);
  const tiers = [
    { limit: 10140, rate: 0.09 },
    { limit: 40960, rate: 0.085 },
    { limit: Infinity, rate: 0.07 },
  ];
  for (const tier of tiers) {
    const used = Math.min(remaining, tier.limit);
    cost += used * tier.rate;
    remaining -= used;
    if (remaining <= 0) break;
  }
  return cost;
}

const objectStorage: CalculatorConfig = {
  type: 'object-storage',
  title: 'Object Storage Cost Calculator',
  description: 'Compare costs across providers and storage tiers. Retrieval fees are estimated from egress volume.',
  inputs: [
    {
      id: 'storageClass',
      label: 'Storage Class',
      unit: '',
      min: 0,
      max: 3,
      step: 1,
      defaultValue: 0,
      options: [
        { label: 'Standard / Hot', value: 0 },
        { label: 'Infrequent Access / Cool / Nearline', value: 1 },
        { label: 'Cold / Glacier Instant / Coldline', value: 2 },
        { label: 'Archive / Glacier Deep Archive', value: 3 },
      ],
    },
    { id: 'storageGB', label: 'Storage', unit: 'GB', min: 0, max: 100000, step: 10, defaultValue: 500 },
    { id: 'egressGB', label: 'Egress', unit: 'GB/mo', min: 0, max: 50000, step: 10, defaultValue: 100 },
    { id: 'putOpsK', label: 'PUT/POST Operations', unit: 'K/mo', min: 0, max: 10000, step: 10, defaultValue: 100 },
    { id: 'getOpsK', label: 'GET Operations', unit: 'K/mo', min: 0, max: 100000, step: 100, defaultValue: 1000 },
  ],
  providers: [
    {
      name: 'Cloudflare R2',
      color: '#f48120',
      calculate: ({ storageClass, storageGB, egressGB, putOpsK, getOpsK }) => {
        // R2 only has Standard and IA — cold/archive fall back to IA
        const tier = r2Tiers[Math.min(storageClass, 1)];
        const storageCost = Math.max(0, storageGB - 10) * tier.storage;
        const retrievalCost = egressGB * tier.retrieval;
        const putCost = Math.max(0, putOpsK - 1000) * tier.putPer1K;
        const getCost = Math.max(0, getOpsK - 10000) * tier.getPer1K;
        return storageCost + retrievalCost + putCost + getCost;
      },
      notes: ({ storageClass }) => {
        if (storageClass >= 2)
          return 'R2 has no cold/archive tier — showing Infrequent Access ($0.01/GB), its lowest class. Zero egress.';
        if (storageClass === 1)
          return 'Infrequent Access: $0.01/GB storage, $0.01/GB retrieval, 30-day min. Zero egress.';
        return 'Zero egress fees. Free tier: 10 GB storage, 1M class A ops, 10M class B ops.';
      },
    },
    {
      name: 'AWS S3',
      color: '#ff9900',
      calculate: ({ storageClass, storageGB, egressGB, putOpsK, getOpsK }) => {
        const tier = s3Tiers[storageClass];
        const storageCost = storageGB * tier.storage;
        const retrievalCost = egressGB * tier.retrieval;
        const egressTotal = s3EgressCost(egressGB);
        const putCost = putOpsK * tier.putPer1K;
        const getCost = getOpsK * tier.getPer1K;
        return storageCost + retrievalCost + egressTotal + putCost + getCost;
      },
      notes: ({ storageClass }) => [
        'S3 Standard. First 100 GB egress/mo free.',
        'S3 Standard-IA. $0.01/GB retrieval. 30-day minimum storage.',
        'Glacier Instant Retrieval. $0.03/GB retrieval. 90-day minimum.',
        'Glacier Deep Archive. $0.02/GB retrieval. 180-day min. 12-48hr access time.',
      ][storageClass],
    },
    {
      name: 'Azure Blob Storage',
      color: '#0078d4',
      calculate: ({ storageClass, storageGB, egressGB, putOpsK, getOpsK }) => {
        const tier = azureTiers[storageClass];
        const storageCost = storageGB * tier.storage;
        const retrievalCost = egressGB * tier.retrieval;
        const egressCost = Math.max(0, egressGB - 100) * 0.087;
        const putCost = putOpsK * tier.putPer1K;
        const getCost = getOpsK * tier.getPer1K;
        return storageCost + retrievalCost + egressCost + putCost + getCost;
      },
      notes: ({ storageClass }) => [
        'Hot tier. First 100 GB egress/mo free.',
        'Cool tier. $0.01/GB retrieval. 30-day minimum storage.',
        'Cold tier. $0.03/GB retrieval. 90-day minimum storage.',
        'Archive tier. $0.022/GB rehydration. 180-day min. Hours to access.',
      ][storageClass],
    },
    {
      name: 'Google Cloud Storage',
      color: '#4285f4',
      calculate: ({ storageClass, storageGB, egressGB, putOpsK, getOpsK }) => {
        const tier = gcsTiers[storageClass];
        const storageCost = storageGB * tier.storage;
        const retrievalCost = egressGB * tier.retrieval;
        const egressCost = Math.max(0, egressGB - 100) * 0.12;
        const putCost = putOpsK * tier.putPer1K;
        const getCost = getOpsK * tier.getPer1K;
        return storageCost + retrievalCost + egressCost + putCost + getCost;
      },
      notes: ({ storageClass }) => [
        'Standard class. First 100 GB egress/mo free.',
        'Nearline. $0.01/GB retrieval. 30-day minimum storage.',
        'Coldline. $0.02/GB retrieval. 90-day minimum storage.',
        'Archive. $0.05/GB retrieval. 365-day min. Instant access with retrieval fee.',
      ][storageClass],
    },
  ],
};

const dns: CalculatorConfig = {
  type: 'dns',
  title: 'DNS Cost Calculator',
  description: 'Compare DNS hosting costs based on zones and query volume.',
  inputs: [
    { id: 'zones', label: 'Hosted Zones', unit: 'zones', min: 1, max: 1000, step: 1, defaultValue: 5 },
    { id: 'queriesM', label: 'DNS Queries', unit: 'million/mo', min: 0, max: 10000, step: 1, defaultValue: 10 },
  ],
  providers: [
    {
      name: 'Cloudflare',
      color: '#f48120',
      calculate: () => 0,
      notes: () => 'Free unlimited DNS hosting, unlimited zones, unlimited queries on all plans.',
    },
    {
      name: 'AWS Route 53',
      color: '#ff9900',
      calculate: ({ zones, queriesM }) => {
        // $0.50/hosted zone (first 25), $0.10 after; $0.40/M queries (first 1B)
        const zoneCost = Math.min(zones, 25) * 0.50 + Math.max(0, zones - 25) * 0.10;
        const queryCost = queriesM * 0.40;
        return zoneCost + queryCost;
      },
      notes: () => '$0.50/zone for first 25, then $0.10. $0.40 per million queries.',
    },
    {
      name: 'Azure DNS',
      color: '#0078d4',
      calculate: ({ zones, queriesM }) => {
        // $0.50/zone, $0.40/M queries (first 1B)
        const zoneCost = zones * 0.50;
        const queryCost = queriesM * 0.40;
        return zoneCost + queryCost;
      },
    },
    {
      name: 'Google Cloud DNS',
      color: '#4285f4',
      calculate: ({ zones, queriesM }) => {
        // $0.20/zone, $0.40/M queries (first 1B)
        const zoneCost = zones * 0.20;
        const queryCost = queriesM * 0.40;
        return zoneCost + queryCost;
      },
      notes: () => 'Lowest per-zone cost among hyperscalers at $0.20/zone.',
    },
  ],
};

const serverless: CalculatorConfig = {
  type: 'serverless',
  title: 'Serverless Compute Cost Calculator',
  description: 'Compare serverless function costs across providers.',
  inputs: [
    { id: 'requestsM', label: 'Requests', unit: 'million/mo', min: 0, max: 1000, step: 1, defaultValue: 10 },
    { id: 'avgCpuMs', label: 'Avg Execution Time', unit: 'ms/request', min: 1, max: 5000, step: 10, defaultValue: 50 },
    { id: 'memoryMB', label: 'Memory', unit: 'MB', min: 128, max: 10240, step: 128, defaultValue: 256 },
  ],
  providers: [
    {
      name: 'Cloudflare Workers',
      color: '#f48120',
      calculate: ({ requestsM, avgCpuMs }) => {
        // Paid plan: $5/mo base, 10M requests included, then $0.30/M
        // CPU time: 30ms included, $0.02/M ms beyond
        const baseCost = 5;
        const requestCost = Math.max(0, requestsM - 10) * 0.30;
        const totalCpuMs = requestsM * 1000000 * avgCpuMs;
        const includedCpuMs = 30 * requestsM * 1000000; // 30ms per request
        const excessCpuMs = Math.max(0, totalCpuMs - includedCpuMs);
        const cpuCost = (excessCpuMs / 1000000) * 0.02;
        return baseCost + requestCost + cpuCost;
      },
      notes: ({ avgCpuMs }) =>
        avgCpuMs <= 30
          ? 'Workers bills CPU time, not wall-clock. Most requests under 30ms CPU are covered in the base plan.'
          : 'Workers bills CPU time (not wall-clock), so I/O wait is free.',
    },
    {
      name: 'AWS Lambda',
      color: '#ff9900',
      calculate: ({ requestsM, avgCpuMs, memoryMB }) => {
        // $0.20/M requests (first 1M free), $0.0000166667/GB-s
        const requestCost = Math.max(0, requestsM - 1) * 0.20;
        // Wall-clock billing: GB-seconds
        const gbSeconds = requestsM * 1000000 * (avgCpuMs / 1000) * (memoryMB / 1024);
        // First 400K GB-s free
        const billableGbS = Math.max(0, gbSeconds - 400000);
        const computeCost = billableGbS * 0.0000166667;
        return requestCost + computeCost;
      },
      notes: () => 'Wall-clock billing (includes I/O wait). Free tier: 1M requests + 400K GB-s/mo.',
    },
    {
      name: 'Azure Functions',
      color: '#0078d4',
      calculate: ({ requestsM, avgCpuMs, memoryMB }) => {
        // Consumption plan: $0.20/M executions (first 1M free), $0.000016/GB-s (first 400K free)
        const requestCost = Math.max(0, requestsM - 1) * 0.20;
        const gbSeconds = requestsM * 1000000 * (avgCpuMs / 1000) * (memoryMB / 1024);
        const billableGbS = Math.max(0, gbSeconds - 400000);
        const computeCost = billableGbS * 0.000016;
        return requestCost + computeCost;
      },
      notes: () => 'Consumption plan pricing. Free tier: 1M executions + 400K GB-s/mo.',
    },
    {
      name: 'Google Cloud Functions',
      color: '#4285f4',
      calculate: ({ requestsM, avgCpuMs, memoryMB }) => {
        // $0.40/M invocations (first 2M free), $0.0000025/GB-s memory, $0.00001/GHz-s CPU
        const requestCost = Math.max(0, requestsM - 2) * 0.40;
        const gbSeconds = requestsM * 1000000 * (avgCpuMs / 1000) * (memoryMB / 1024);
        const memoryCost = Math.max(0, gbSeconds - 400000) * 0.0000025;
        // CPU: assume 1 GHz per 256MB
        const ghzSeconds = requestsM * 1000000 * (avgCpuMs / 1000) * (memoryMB / 256);
        const cpuCost = Math.max(0, ghzSeconds - 200000) * 0.00001;
        return requestCost + memoryCost + cpuCost;
      },
      notes: () => 'Free tier: 2M invocations, 400K GB-s, 200K GHz-s/mo.',
    },
  ],
};

const ddosWaf: CalculatorConfig = {
  type: 'ddos-waf',
  title: 'DDoS + WAF Cost Calculator',
  description: 'Compare web security protection costs across providers.',
  inputs: [
    { id: 'requestsM', label: 'Web Requests', unit: 'million/mo', min: 0, max: 10000, step: 10, defaultValue: 100 },
    { id: 'wafRules', label: 'Custom WAF Rules', unit: 'rules', min: 0, max: 100, step: 1, defaultValue: 10 },
    { id: 'protectedResources', label: 'Protected Resources', unit: 'resources', min: 1, max: 50, step: 1, defaultValue: 3 },
  ],
  providers: [
    {
      name: 'Cloudflare',
      color: '#f48120',
      calculate: () => 0,
      notes: () => 'Free plan: unlimited DDoS protection + 5 WAF custom rules. Pro ($20/mo): managed rulesets + 20 rules. Business ($200/mo): advanced WAF + 100 rules.',
    },
    {
      name: 'AWS Shield + WAF',
      color: '#ff9900',
      calculate: ({ requestsM, wafRules, protectedResources }) => {
        // Shield Standard: free. WAF: $5/web ACL + $1/rule + $0.60/M requests
        // Shield Advanced: $3,000/mo (not included here for fair comparison)
        const wafBaseCost = protectedResources * 5; // $5/web ACL
        const ruleCost = wafRules * 1;
        const requestCost = requestsM * 0.60;
        return wafBaseCost + ruleCost + requestCost;
      },
      notes: () => 'Shield Standard (free DDoS) + WAF pricing. Shield Advanced adds $3,000/mo for enhanced protection.',
    },
    {
      name: 'Azure DDoS + WAF',
      color: '#0078d4',
      calculate: ({ requestsM, protectedResources }) => {
        // DDoS Network Protection: $2,944/mo + $30/resource. WAF on Front Door included
        // Using WAF on Application Gateway: per-gateway pricing
        // Simpler: DDoS IP Protection at $199/IP/mo
        const ddosCost = protectedResources * 199;
        // Front Door WAF: $5/policy + $1/custom rule + request charges
        const wafBaseCost = 20; // base policy cost
        const requestCost = requestsM * 0.60;
        return ddosCost + wafBaseCost + requestCost;
      },
      notes: () => 'DDoS IP Protection ($199/IP) shown. DDoS Network Protection: $2,944/mo flat + $30/resource.',
    },
    {
      name: 'Google Cloud Armor',
      color: '#4285f4',
      calculate: ({ requestsM, wafRules }) => {
        // Standard: $5/policy + $1/rule + $0.75/M requests
        const policyCost = 5;
        const ruleCost = wafRules * 1;
        const requestCost = requestsM * 0.75;
        return policyCost + ruleCost + requestCost;
      },
      notes: () => 'Cloud Armor Standard pricing. Managed Protection Plus: ~$3,000/mo.',
    },
  ],
};

const loadBalancing: CalculatorConfig = {
  type: 'load-balancing',
  title: 'Load Balancing Cost Calculator',
  description: 'Compare load balancing costs based on your traffic and origin setup.',
  inputs: [
    { id: 'origins', label: 'Origin Servers', unit: 'origins', min: 1, max: 50, step: 1, defaultValue: 3 },
    { id: 'healthChecks', label: 'Health Check Freq', unit: 'checks/min', min: 1, max: 12, step: 1, defaultValue: 4 },
    { id: 'dataProcessedGB', label: 'Data Processed', unit: 'GB/mo', min: 0, max: 50000, step: 10, defaultValue: 500 },
  ],
  providers: [
    {
      name: 'Cloudflare',
      color: '#f48120',
      calculate: ({ origins, healthChecks }) => {
        // $5/mo base + $5/additional origin + health checks
        // First origin included
        const baseCost = 5;
        const originCost = Math.max(0, origins - 2) * 5;
        // Health check intervals: 60s included, faster intervals cost more
        const healthCheckCost = healthChecks > 1 ? (healthChecks - 1) * 3 : 0;
        return baseCost + originCost + healthCheckCost;
      },
      notes: () => 'Simple per-origin pricing. Includes geo-steering, failover, and session affinity.',
    },
    {
      name: 'AWS ALB',
      color: '#ff9900',
      calculate: ({ dataProcessedGB }) => {
        // ~$22.27/mo base (730 hrs * $0.0225 + $0.008/LCU-hr)
        const baseCost = 730 * 0.0225;
        // LCU: $0.008/hr, estimate based on data processed
        // 1 LCU = 1 GB/hr new connections or 25 connections/sec
        const lcuHours = (dataProcessedGB / 730) * 730; // simplified
        const lcuCost = lcuHours * 0.008;
        return baseCost + lcuCost;
      },
      notes: () => 'Hourly base rate + LCU usage charges. Data transfer out billed separately.',
    },
    {
      name: 'Azure Front Door',
      color: '#0078d4',
      calculate: ({ dataProcessedGB }) => {
        // Front Door Standard: $35/mo base + data transfer
        const baseCost = 35;
        const dataCost = dataProcessedGB * 0.081;
        return baseCost + dataCost;
      },
      notes: () => 'Front Door Standard includes global load balancing, CDN, and WAF.',
    },
    {
      name: 'Google Cloud LB',
      color: '#4285f4',
      calculate: ({ dataProcessedGB }) => {
        // $0.025/hr per forwarding rule + $0.008/GB data processed
        // First 5 forwarding rules: $0.025/hr each
        const baseCost = 730 * 0.025; // 1 rule, hourly
        const dataCost = dataProcessedGB * 0.008;
        return baseCost + dataCost;
      },
      notes: () => 'Per-rule hourly pricing + data processing charges.',
    },
  ],
};

const zeroTrust: CalculatorConfig = {
  type: 'zero-trust',
  title: 'Zero Trust / ZTNA Cost Calculator',
  description: 'Compare zero trust network access costs per user.',
  inputs: [
    { id: 'users', label: 'Users', unit: 'users', min: 1, max: 10000, step: 10, defaultValue: 100 },
  ],
  providers: [
    {
      name: 'Cloudflare Access',
      color: '#f48120',
      calculate: ({ users }) => {
        // Free for first 50 users, then $7/user/mo
        if (users <= 50) return 0;
        return (users - 50) * 7;
      },
      notes: ({ users }) =>
        users <= 50
          ? 'Free for up to 50 users. Includes ZTNA, DNS filtering, and secure web gateway.'
          : 'First 50 users free, then $7/user/mo. Includes ZTNA, SWG, and CASB.',
    },
    {
      name: 'AWS Verified Access',
      color: '#ff9900',
      calculate: ({ users }) => {
        // $0.27/hr per instance + data processing fees
        // Estimate: 1 instance per 100 users
        const instances = Math.ceil(users / 100);
        return instances * 730 * 0.27;
      },
      notes: () => 'Per-instance hourly pricing ($0.27/hr). Requires separate VPN or Direct Connect for some use cases.',
    },
    {
      name: 'Azure Entra ID P2',
      color: '#0078d4',
      calculate: ({ users }) => {
        // $9/user/mo for Entra ID P2 (includes conditional access, PIM)
        return users * 9;
      },
      notes: () => '$9/user/mo for Entra ID P2. Includes conditional access, identity protection, and PIM.',
    },
    {
      name: 'Google BeyondCorp',
      color: '#4285f4',
      calculate: ({ users }) => {
        // BeyondCorp Enterprise: ~$6/user/mo
        return users * 6;
      },
      notes: () => '~$6/user/mo for BeyondCorp Enterprise. Integrated with Chrome browser.',
    },
  ],
};

const edgeDatabases: CalculatorConfig = {
  type: 'edge-databases',
  title: 'Edge Database Cost Calculator',
  description: 'Compare database costs for edge and serverless workloads.',
  inputs: [
    { id: 'storageGB', label: 'Storage', unit: 'GB', min: 0, max: 1000, step: 1, defaultValue: 5 },
    { id: 'readsM', label: 'Read Operations', unit: 'million/mo', min: 0, max: 1000, step: 1, defaultValue: 10 },
    { id: 'writesM', label: 'Write Operations', unit: 'million/mo', min: 0, max: 100, step: 0.1, defaultValue: 1 },
  ],
  providers: [
    {
      name: 'Cloudflare D1 + KV',
      color: '#f48120',
      calculate: ({ storageGB, readsM, writesM }) => {
        // D1: $0.75/M reads (first 25B free), $1.00/M writes (first 50M free), $0.75/GB storage (first 5GB free)
        // KV: $0.50/M reads (first 10M free), $5/M writes (first 1M free), $0.50/GB storage (first 1GB free)
        // Using D1 pricing as primary
        const storageCost = Math.max(0, storageGB - 5) * 0.75;
        const readCost = Math.max(0, readsM - 25) * 0.75;
        const writeCost = Math.max(0, writesM - 0.05) * 1.00;
        return storageCost + readCost + writeCost;
      },
      notes: () => 'D1 pricing shown. Generous free tier: 5 GB storage, 25B reads, 50M writes/mo.',
    },
    {
      name: 'AWS DynamoDB',
      color: '#ff9900',
      calculate: ({ storageGB, readsM, writesM }) => {
        // On-demand: $1.25/M write request units, $0.25/M read request units
        // Storage: $0.25/GB (first 25GB free)
        const storageCost = Math.max(0, storageGB - 25) * 0.25;
        const readCost = readsM * 0.25;
        const writeCost = writesM * 1.25;
        return storageCost + readCost + writeCost;
      },
      notes: () => 'On-demand pricing. First 25 GB storage free. Provisioned capacity cheaper at steady-state.',
    },
    {
      name: 'Azure Cosmos DB',
      color: '#0078d4',
      calculate: ({ storageGB, readsM, writesM }) => {
        // Serverless: $0.25/M RUs. Estimate: 1 read = 1 RU, 1 write = 5 RUs
        // Storage: $0.25/GB
        const storageCost = storageGB * 0.25;
        const readRUs = readsM * 1000000; // 1 RU per read
        const writeRUs = writesM * 1000000 * 5; // 5 RUs per write
        const totalRUs = readRUs + writeRUs;
        const ruCost = (totalRUs / 1000000) * 0.25;
        return storageCost + ruCost;
      },
      notes: () => 'Serverless mode pricing. 1 read = ~1 RU, 1 write = ~5 RUs.',
    },
    {
      name: 'Google Firestore',
      color: '#4285f4',
      calculate: ({ storageGB, readsM, writesM }) => {
        // $0.036/100K reads, $0.108/100K writes, $0.108/GB storage
        // Free tier: 50K reads, 20K writes, 1GB storage/day
        const storageCost = Math.max(0, storageGB - 1) * 0.108;
        const readCost = Math.max(0, readsM * 1000 - 50) * 0.036; // per 100K
        const writeCost = Math.max(0, writesM * 1000 - 20) * 0.108; // per 100K
        return storageCost + readCost + writeCost;
      },
      notes: () => 'Free tier: 50K reads, 20K writes, 1 GB storage per day.',
    },
  ],
};

const email: CalculatorConfig = {
  type: 'email',
  title: 'Email Service Cost Calculator',
  description: 'Compare email service costs based on volume and mailbox needs.',
  inputs: [
    { id: 'emailsSentK', label: 'Emails Sent', unit: 'K/mo', min: 0, max: 10000, step: 10, defaultValue: 50 },
    { id: 'mailboxUsers', label: 'Mailbox Users', unit: 'users', min: 0, max: 1000, step: 1, defaultValue: 10 },
  ],
  providers: [
    {
      name: 'Cloudflare Email',
      color: '#f48120',
      calculate: () => 0,
      notes: () => 'Free email routing (receive + forward). No sending service — pair with a transactional sender.',
    },
    {
      name: 'AWS SES',
      color: '#ff9900',
      calculate: ({ emailsSentK }) => {
        // $0.10 per 1,000 emails
        return emailsSentK * 0.10;
      },
      notes: () => 'Transactional sending only. $0.10 per 1,000 emails. No mailbox hosting.',
    },
    {
      name: 'Azure Communication',
      color: '#0078d4',
      calculate: ({ emailsSentK }) => {
        // First 100 emails free, then $0.00025/email ($0.25/1K)
        const emails = emailsSentK * 1000;
        return Math.max(0, emails - 100) * 0.00025;
      },
      notes: () => 'Azure Communication Services. $0.25 per 1,000 emails. First 100/mo free.',
    },
    {
      name: 'Google Workspace',
      color: '#4285f4',
      calculate: ({ mailboxUsers }) => {
        // $7.20/user/mo for Business Starter (includes Gmail mailbox + sending)
        return mailboxUsers * 7.20;
      },
      notes: () => '$7.20/user/mo (Business Starter). Includes Gmail, Drive, Meet, and full productivity suite.',
    },
  ],
};

const containers: CalculatorConfig = {
  type: 'containers',
  title: 'Container Compute Cost Calculator',
  description: 'Compare container hosting costs for your workloads.',
  inputs: [
    { id: 'vcpuHours', label: 'vCPU Usage', unit: 'vCPU-hours/mo', min: 0, max: 100000, step: 100, defaultValue: 1000 },
    { id: 'memoryGBHours', label: 'Memory Usage', unit: 'GB-hours/mo', min: 0, max: 200000, step: 100, defaultValue: 2000 },
  ],
  providers: [
    {
      name: 'Cloudflare Workers',
      color: '#f48120',
      calculate: ({ vcpuHours }) => {
        // Workers pricing: $0.02/M CPU-ms = $72/vCPU-hr equivalent (but much cheaper for short tasks)
        // Container-like: estimate based on CPU hours
        // $5/mo base + included usage
        const baseCost = 5;
        // 30ms CPU per request, 10M requests = 83.3 CPU-hours included
        const includedHours = 83.3;
        const excessHours = Math.max(0, vcpuHours - includedHours);
        // $0.02/M ms = $0.072/hr
        const computeCost = excessHours * 0.072;
        return baseCost + computeCost;
      },
      notes: () => 'Workers are not traditional containers but handle many container use cases. CPU-time billing only (I/O wait is free).',
    },
    {
      name: 'AWS Fargate',
      color: '#ff9900',
      calculate: ({ vcpuHours, memoryGBHours }) => {
        // $0.04048/vCPU-hr + $0.004445/GB-hr
        return vcpuHours * 0.04048 + memoryGBHours * 0.004445;
      },
      notes: () => 'Serverless containers. No cluster management. Per-second billing.',
    },
    {
      name: 'Azure AKS',
      color: '#0078d4',
      calculate: ({ vcpuHours, memoryGBHours }) => {
        // AKS control plane free, node costs ~$0.043/vCPU-hr (D-series VMs)
        // Estimate memory at $0.0047/GB-hr
        return vcpuHours * 0.043 + memoryGBHours * 0.0047;
      },
      notes: () => 'AKS control plane is free. Pay for VM node costs only. Pricing based on D-series VMs.',
    },
    {
      name: 'Google GKE / Cloud Run',
      color: '#4285f4',
      calculate: ({ vcpuHours, memoryGBHours }) => {
        // Cloud Run: $0.00002400/vCPU-s = $0.0864/vCPU-hr + $0.00000250/GiB-s = $0.009/GB-hr
        // GKE Autopilot: $0.0445/vCPU-hr + $0.0049375/GB-hr
        // Using Cloud Run as closer serverless comparison
        return vcpuHours * 0.0864 + memoryGBHours * 0.009;
      },
      notes: () => 'Cloud Run pricing shown (serverless containers). GKE Autopilot is ~50% cheaper for sustained workloads.',
    },
  ],
};

export const calculatorConfigs: Record<string, CalculatorConfig> = {
  cdn,
  'object-storage': objectStorage,
  dns,
  serverless,
  'ddos-waf': ddosWaf,
  'load-balancing': loadBalancing,
  'zero-trust': zeroTrust,
  'edge-databases': edgeDatabases,
  email,
  containers,
};
